import { useState } from 'react'
import { useBasket } from '../state/BasketContext'
import { useTranslation } from '../i18n/LanguageContext'
import { DEFAULT_USER_PROFILE } from '../types'

export function GlobalActions() {
  const {
    customConstraints,
    customPortions,
    customPurchaseInfo,
    resetNutrientConstraint,
    resetCustomPortion,
    resetCustomPurchaseInfo,
    clearBasket,
    setUserProfile,
    exportPreferences,
    importPreferences
  } = useBasket()
  const { t } = useTranslation()

  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState<'export' | 'import'>('export')
  const [modalData, setModalData] = useState('')
  const [importError, setImportError] = useState('')

  const handleExport = () => {
    setModalMode('export')
    setModalData(exportPreferences())
    setImportError('')
    setShowModal(true)
  }

  const handleImportClick = () => {
    setModalMode('import')
    setModalData('')
    setImportError('')
    setShowModal(true)
  }

  const handleImport = () => {
    try {
      importPreferences(modalData)
      setShowModal(false)
      setModalData('')
      setImportError('')
    } catch {
      setImportError(t('globalActions.modal.invalidFormat'))
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setModalData('')
    setImportError('')
  }

  const handleResetAll = () => {
    clearBasket()
    setUserProfile(DEFAULT_USER_PROFILE)
    Object.keys(customConstraints).forEach(nutrientId => {
      resetNutrientConstraint(parseInt(nutrientId))
    })
    Object.keys(customPortions).forEach(foodId => {
      resetCustomPortion(foodId)
    })
    Object.keys(customPurchaseInfo).forEach(foodId => {
      resetCustomPurchaseInfo(foodId)
    })
  }

  return (
    <>
      <div className="d-flex align-items-center gap-2">
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={handleResetAll}
          title={t('globalActions.resetAllTooltip')}
        >
          <i className="bi bi-arrow-counterclockwise me-1"></i>{t('globalActions.resetAllLabel')}
        </button>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={handleExport}
          title={t('globalActions.exportTooltip')}
        >
          <i className="bi bi-download me-1"></i>{t('globalActions.exportLabel')}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={handleImportClick}
          title={t('globalActions.importTooltip')}
        >
          <i className="bi bi-upload me-1"></i>{t('globalActions.importLabel')}
        </button>
      </div>

      {showModal && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className={`bi ${modalMode === 'export' ? 'bi-download' : 'bi-upload'} me-2`}></i>
                    {modalMode === 'export' ? t('globalActions.modal.exportTitle') : t('globalActions.modal.importTitle')}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeModal}></button>
                </div>
                <div className="modal-body">
                  {modalMode === 'export' ? (
                    <div>
                      <p className="text-muted mb-3">
                        {t('globalActions.modal.exportInstructions')}
                      </p>
                      <textarea
                        className="form-control font-monospace"
                        value={modalData}
                        readOnly
                        rows={8}
                        style={{ fontSize: '12px', lineHeight: '1.4' }}
                      />
                      <div className="d-flex justify-content-end mt-3">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            navigator.clipboard.writeText(modalData)
                          }}
                        >
                          <i className="bi bi-clipboard me-1"></i>{t('globalActions.modal.copyToClipboard')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-muted mb-3">
                        {t('globalActions.modal.importInstructions')}
                      </p>
                      <textarea
                        className="form-control font-monospace"
                        value={modalData}
                        onChange={(e) => setModalData(e.target.value)}
                        rows={8}
                        style={{ fontSize: '12px', lineHeight: '1.4' }}
                        placeholder={t('globalActions.modal.importPlaceholder')}
                      />
                      {importError && (
                        <div className="alert alert-danger mt-3 mb-0">
                          <i className="bi bi-exclamation-triangle me-2"></i>
                          {importError}
                        </div>
                      )}
                      <div className="d-flex justify-content-end gap-2 mt-3">
                        <button
                          className="btn btn-secondary"
                          onClick={closeModal}
                        >
                          {t('globalActions.modal.cancel')}
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={handleImport}
                          disabled={!modalData.trim()}
                        >
                          <i className="bi bi-upload me-1"></i>{t('globalActions.modal.loadButton')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
